import { ChevronDown, Search } from 'lucide-react';
import * as React from 'react';
import supabase from '../../api/supabase';

const BUCKET_NAME = 'Files';
const FOLDER_NAME = 'Avatar';

interface Avatar {
  label: string;
  value: string;
  url: string;
}

interface AvatarDropdownProps {
  onSelect?: (value: string) => void;
  defaultValue?: string;
}

export function AvatarDropdown({ onSelect, defaultValue }: AvatarDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [value, setValue] = React.useState(defaultValue || '');
  const [search, setSearch] = React.useState('');
  const [avatars, setAvatars] = React.useState<Avatar[]>([]);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch avatars
  const fetchFiles = React.useCallback(async () => {
    try {
      const { data, error } = await supabase.storage.from(BUCKET_NAME).list(FOLDER_NAME);

      if (error) {
        console.error('Error fetching files:', error);
        return;
      }

      if (!data || data.length === 0) {
        console.log('No files found in the folder');
        return;
      }

      const fileList = await Promise.all(
        data
          .filter(file => !file.metadata?.isFolder)
          .map(async file => {
            const fileUrl = supabase.storage
              .from(BUCKET_NAME)
              .getPublicUrl(`${FOLDER_NAME}/${file.name}`);

            return {
              label: file.name,
              value: fileUrl.data.publicUrl,
              url: fileUrl.data.publicUrl,
            };
          })
      );

      setAvatars(fileList);

      // If we have a defaultValue, find and select the matching avatar
      if (defaultValue) {
        const defaultAvatar = fileList.find(avatar => avatar.value === defaultValue);
        if (defaultAvatar) {
          setValue(defaultAvatar.value);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [defaultValue]);

  React.useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleSelect = (avatar: Avatar) => {
    setValue(avatar.value);
    onSelect?.(avatar.value);
    setSearch('');
    setIsOpen(false);
  };

  const filteredAvatars = avatars.filter(avatar =>
    avatar.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedAvatar = avatars.find(avatar => avatar.value === value);

  return (
    <div className="relative w-[165px] md:w-[300px]" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-md bg-white hover:bg-gray-50"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedAvatar ? (
            <>
              <img
                src={selectedAvatar.url}
                alt={selectedAvatar.label}
                className="h-6 w-6 rounded-full object-cover flex-shrink-0"
              />
              <span className="truncate">{selectedAvatar.label}</span>
            </>
          ) : (
            <span className="text-gray-500">Select avatar...</span>
          )}
        </div>
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search avatars..."
                className="w-full pl-8 pr-3 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Avatar List */}
          <div className="max-h-[300px] overflow-y-auto p-1">
            {filteredAvatars.length === 0 ? (
              <div className="text-center py-2 text-gray-500">No avatars found</div>
            ) : (
              filteredAvatars.map(avatar => (
                <button
                  type="button"
                  key={avatar.value}
                  onClick={() => handleSelect(avatar)}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100"
                >
                  <img
                    src={avatar.url}
                    alt={avatar.label}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                  <span className="flex-1 text-left truncate">{avatar.label}</span>
                  {value === avatar.value && <div className="h-2 w-2 rounded-full bg-blue-500" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
